import React, { ChangeEvent, KeyboardEvent, PureComponent } from 'react'
import { DispatchProp } from 'react-redux'
import { AnyAction } from 'redux'
import { Modal } from '../../components'
import { connectRedux } from '../../helpers/connectRedux'
import { images } from '../../images'
import { ICountry } from '../../models/country'
import { phoneCountries } from '../../phoneCountries'
import { LiteralsService } from '../../services'
import { setToggleCountryPickerModalAction } from '../../store/actions'
import { IStoreState } from '../../store/states'
import StyledDiv from './styles'

interface IOwnProps {
  selectCountry: (country: ICountry) => void
  // The country currently picked by the form that opens the picker, shown as selected
  selectedCountry?: ICountry
}

interface IProps extends IOwnProps, DispatchProp<AnyAction> {
  isCountryPickerModalOpen: boolean
}

interface IState {
  searchValue: string
}

interface ICountryOption {
  country: ICountry
  label: string
  searchText: string
}

// Our 'si' is Slovenian, whose ISO code is 'sl'
const intlLanguage = (language: string) => language === 'si' ? 'sl' : language

// Lowercase and without accents, so "espana" finds "España"
const normalize = (text: string) => text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()

class CountryPickerModal extends PureComponent<IProps, IState> {
  public state: IState = { searchValue: '' }

  // Country names in the interface language, alphabetically
  private get options(): ICountryOption[] {
    const language = intlLanguage(LiteralsService.getLanguage())
    let displayNames: Intl.DisplayNames | null = null
    try {
      displayNames = new Intl.DisplayNames([language], { type: 'region' })
    } catch (error) {
      // Browsers without Intl.DisplayNames keep the English names
    }

    return phoneCountries
      .map(country => {
        const label = displayNames?.of(country.code) || country.name
        return { country, label, searchText: normalize(`${label} ${country.name} ${country.dial_code}`) }
      })
      .sort((a, b) => a.label.localeCompare(b.label, language))
  }

  private get filteredOptions(): ICountryOption[] {
    const search = normalize(this.state.searchValue.trim())
    if (!search) return this.options
    return this.options.filter(option => option.searchText.includes(search))
  }

  public render() {
    const { isCountryPickerModalOpen, selectedCountry } = this.props
    const { searchValue } = this.state

    if (!isCountryPickerModalOpen) return null

    const options = this.filteredOptions

    return (
      <Modal isHidden={ false } onOverlayClick={ this.handleClose } style={{ padding: 0 }}>
        <StyledDiv role="dialog" aria-modal="true" aria-labelledby="country-picker-title">
          <header>
            <h3 id="country-picker-title">{ LiteralsService.get('selectCountry', true) }</h3>
            <button
              type="button"
              className="close"
              onClick={ this.handleClose }
              aria-label={ LiteralsService.get('close', true) }
            >
              <img src={ images['cross'] } alt="" />
            </button>
          </header>
          <div className="search">
            <img src={ images['lens'] } alt="" />
            <input
              type="search"
              value={ searchValue }
              onChange={ this.handleSearchChange }
              onKeyDown={ this.handleKeyDown }
              placeholder={ LiteralsService.get('searchCountry', true) }
              aria-label={ LiteralsService.get('searchCountry', true) }
              autoFocus
            />
          </div>
          <ul className="countries">
            {
              options.map(({ country, label }) => {
                const isSelected = selectedCountry?.code === country.code
                return (
                  <li key={ country.code }>
                    <button
                      type="button"
                      className={ isSelected ? 'selected' : '' }
                      onClick={ () => this.handleCountryClick(country) }
                      aria-pressed={ isSelected }
                    >
                      <span className="flag" aria-hidden="true">{ country.emoji }</span>
                      <span className="name">{ label }</span>
                      <span className="dial-code">{ country.dial_code }</span>
                    </button>
                  </li>
                )
              })
            }
            { !options.length && <li className="empty">{ LiteralsService.get('noResults', true) }</li> }
          </ul>
        </StyledDiv>
      </Modal>
    )
  }

  private handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    this.setState({ searchValue: event.target.value })
  }

  // Escape closes the picker; Enter picks the only match left
  private handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') this.handleClose()
    if (event.key === 'Enter') {
      const options = this.filteredOptions
      if (options.length === 1) this.handleCountryClick(options[0].country)
    }
  }

  private handleCountryClick = (country: ICountry) => {
    const { selectCountry } = this.props
    this.handleClose()
    selectCountry(country)
  }

  private handleClose = () => {
    const { dispatch } = this.props
    this.setState({ searchValue: '' })
    dispatch(setToggleCountryPickerModalAction())
  }
}

const mapStateToProps = ({ signUp }: IStoreState) => ({
  isCountryPickerModalOpen: signUp.isCountryPickerModalOpen
})

export default connectRedux(mapStateToProps, CountryPickerModal as any) as React.ComponentType<IOwnProps>
